import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePreferences } from '@/auth/hooks/usePreferences';
import otrosTramitesService, {
  otrosTramitesService as defaultOtrosTramitesService,
  otrosTramitesRetiroFilters,
  otrosTramitesCobroFilters,
  otrosTramitesFacturaFilters,
  otrosTramitesTipoFilters,
} from '../services/otrosTramitesService';
import type {
  CreateOtroTramitePayload,
  ListOtrosTramitesParams,
  OtroTramiteRecord,
  OtroTramiteRetiroFilter,
  OtroTramiteCobroFilter,
  OtroTramiteFacturaFilter,
  OtroTramiteSortDirection,
  OtroTramiteSortField,
  OtroTramiteTipoFilter,
  UserOption,
  UpdateOtroTramitePayload,
} from '../types';

const QUERY_KEY = 'otros-tramites';
const SEARCH_DEBOUNCE_MS = 350;

const defaultSummary = Object.freeze({
  total: 0,
  pendientesCobro: 0,
});

const DEFAULT_SORT_FIELD: OtroTramiteSortField = 'fecha';
const DEFAULT_SORT_DIRECTION: OtroTramiteSortDirection = 'desc';
const SORT_FIELDS: OtroTramiteSortField[] = ['id', 'fecha', 'otorgantes', 'responsables', 'garantes', 'tipo'];
const SORT_DIRECTIONS: OtroTramiteSortDirection[] = ['asc', 'desc'];

const computeSummary = (records: OtroTramiteRecord[]) => ({
  total: records.length,
  pendientesCobro: records.filter((record) => record.cobroARS === 'Sin cobrar' || record.cobroUSD === 'Sin cobrar').length,
});

const isSortField = (value: string | undefined): value is OtroTramiteSortField =>
  Boolean(value && SORT_FIELDS.includes(value as OtroTramiteSortField));

const isSortDirection = (value: string | undefined): value is OtroTramiteSortDirection =>
  Boolean(value && SORT_DIRECTIONS.includes(value as OtroTramiteSortDirection));

const isValidAssignedUser = (value: unknown): value is UserOption => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<UserOption>;
  return typeof candidate.id === 'string' && typeof candidate.nombre === 'string';
};

export interface UseOtrosTramitesOptions {
  service?: typeof defaultOtrosTramitesService;
  initialFilters?: ListOtrosTramitesParams;
}

type SavedOtrosTramitesFilters = {
  sortField?: string;
  sortDirection?: string;
  assignedUserId?: string;
  assignedUser?: UserOption | null;
};

export const useOtrosTramites = (options: UseOtrosTramitesOptions = {}) => {
  const {
    service = otrosTramitesService,
    initialFilters = {},
  } = options;

  const {
    preferences,
    updatePartialPreference,
    isLoading: preferencesLoading,
  } = usePreferences();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(initialFilters.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(initialFilters.search ?? '');
  const [retiro, setRetiro] = useState<OtroTramiteRetiroFilter>(initialFilters.retiro ?? 'all');
  const [cobro, setCobro] = useState<OtroTramiteCobroFilter>(initialFilters.cobro ?? 'all');
  const [factura, setFactura] = useState<OtroTramiteFacturaFilter>(initialFilters.factura ?? 'all');
  const [tipo, setTipo] = useState<OtroTramiteTipoFilter>(initialFilters.tipo ?? 'all');
  const [assignedUserId, setAssignedUserId] = useState(initialFilters.assignedUserId ?? '');
  const [assignedUser, setAssignedUserState] = useState<UserOption | null>(null);
  const [sortField, setSortField] = useState<OtroTramiteSortField>(initialFilters.sort ?? DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState<OtroTramiteSortDirection>(initialFilters.order ?? DEFAULT_SORT_DIRECTION);
  const [isReady, setIsReady] = useState(false);
  const hasSyncedFromPreferences = useRef(false);
  const hasMounted = useRef(false);
  const saveSortPreferencesRef = useRef((nextSortField: OtroTramiteSortField, nextSortDirection: OtroTramiteSortDirection) => {
    updatePartialPreference('otrosTramites.filters', {
      sortField: nextSortField,
      sortDirection: nextSortDirection,
      assignedUserId,
      assignedUser,
    });
  });

  saveSortPreferencesRef.current = (nextSortField: OtroTramiteSortField, nextSortDirection: OtroTramiteSortDirection) => {
    updatePartialPreference('otrosTramites.filters', {
      sortField: nextSortField,
      sortDirection: nextSortDirection,
      assignedUserId,
      assignedUser,
    });
  };

  useEffect(() => {
    if (hasSyncedFromPreferences.current || preferencesLoading) {
      return;
    }

    const savedFilters = (preferences?.otrosTramites?.filters ?? {}) as SavedOtrosTramitesFilters;
    const nextSortField = isSortField(savedFilters?.sortField)
      ? savedFilters.sortField
      : initialFilters.sort ?? DEFAULT_SORT_FIELD;
    const nextSortDirection = isSortDirection(savedFilters?.sortDirection)
      ? savedFilters.sortDirection
      : initialFilters.order ?? DEFAULT_SORT_DIRECTION;
    const nextAssignedUserId = typeof savedFilters?.assignedUserId === 'string'
      ? savedFilters.assignedUserId
      : initialFilters.assignedUserId ?? '';
    const nextAssignedUser = isValidAssignedUser(savedFilters?.assignedUser)
      ? savedFilters.assignedUser
      : null;

    setSortField(nextSortField);
    setSortDirection(nextSortDirection);
    setAssignedUserId(nextAssignedUserId);
    setAssignedUserState(nextAssignedUser?.id === nextAssignedUserId ? nextAssignedUser : null);
    hasSyncedFromPreferences.current = true;
    setIsReady(true);
  }, [preferencesLoading, preferences, initialFilters.sort, initialFilters.order, initialFilters.assignedUserId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  useEffect(() => {
    if (!hasSyncedFromPreferences.current) {
      return;
    }

    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    saveSortPreferencesRef.current(sortField, sortDirection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection, assignedUserId, assignedUser]);

  const setAssignedUser = useCallback((user: UserOption | null) => {
    setAssignedUserId(user?.id ?? '');
    setAssignedUserState(user);
  }, []);

  const listQueryKey = useMemo(
    () => [QUERY_KEY, { search: debouncedSearch, retiro, cobro, factura, tipo, assignedUserId, sortField, sortDirection }],
    [debouncedSearch, retiro, cobro, factura, tipo, assignedUserId, sortField, sortDirection]
  );

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => service.list({ search: debouncedSearch, retiro, cobro, factura, tipo, assignedUserId, sort: sortField, order: sortDirection }),
    staleTime: 1000 * 30,
    enabled: isReady && !preferencesLoading,
  });

  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateOtroTramitePayload) => service.create(payload),
    onSuccess: invalidateList,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOtroTramitePayload }) => service.update(id, payload),
    onSuccess: invalidateList,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => service.remove(id),
    onSuccess: invalidateList,
  });

  const updateMontosMutation = useMutation({
    mutationFn: ({ id, montoARS, montoUSD }: { id: string; montoARS: number; montoUSD: number }) =>
      service.updateMontos(id, montoARS, montoUSD),
    onSuccess: (result) => {
      queryClient.setQueryData(listQueryKey, (current: any) => {
        if (!current) return current;
        const nextData = (current.data as OtroTramiteRecord[]).map((item: OtroTramiteRecord) =>
          item.id === result.id ? result : item,
        );
        return {
          ...current,
          data: nextData,
          summary: computeSummary(nextData),
        };
      });
    },
  });

  const tramites = listQuery.data?.data ?? [];
  const summary = listQuery.data?.summary ?? defaultSummary;

  return {
    tramites,
    summary,
    search,
    setSearch,
    retiro,
    setRetiro,
    cobro,
    setCobro,
    factura,
    setFactura,
    tipo,
    setTipo,
    assignedUserId,
    setAssignedUserId,
    assignedUser,
    setAssignedUser,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    filters: { search, retiro, cobro, factura, tipo, assignedUserId, sort: sortField, order: sortDirection },
    listQuery,
    retiroOptions: otrosTramitesRetiroFilters,
    cobroOptions: otrosTramitesCobroFilters,
    facturaOptions: otrosTramitesFacturaFilters,
    tipoOptions: otrosTramitesTipoFilters,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    updateMontos: updateMontosMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
    isLoading: preferencesLoading || listQuery.isLoading,
    isFetching: listQuery.isFetching,
    isCreating: createMutation.isPending,
    createError: (createMutation.error as Error | null) ?? null,
    isUpdating: updateMutation.isPending,
    updateError: (updateMutation.error as Error | null) ?? null,
    isUpdatingMontos: updateMontosMutation.isPending,
    updateMontosError: (updateMontosMutation.error as Error | null) ?? null,
    isRemoving: deleteMutation.isPending,
    removeError: (deleteMutation.error as Error | null) ?? null,
  };
};

export type UseOtrosTramitesResult = ReturnType<typeof useOtrosTramites>;
